import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        // Semantic variants
        primary:
          'bg-blue-50 border-blue-200 text-blue-900 [&_p]:text-blue-900!',
        secondary:
          'bg-gray-50 border-gray-200 text-gray-900 [&_p]:text-gray-900!',
        warning:
          'bg-amber-50 border-amber-200 text-amber-900 [&_p]:text-amber-900!',
        success:
          'bg-green-50 border-green-200 text-green-900 [&_p]:text-green-900!',
        // Info is a duplicate of primary - do we need both?
        info: 'bg-blue-50 border-blue-200 text-blue-900 [&_p]:text-blue-900!',
        // the below don't follow the pattern of tyhe variants above - is this intentional?
        destructive:
          'text-destructive bg-card [&>svg]:text-current *:data-[slot=alert-description]:text-destructive/90',
        critical:
          'text-white bg-red-500 [&>svg]:text-current *:data-[slot=alert-description]:text-white',
        // Faux "spinning gradient border": a full-bleed radial gradient in a
        // `::before` that rotates, with a white `::after` inset by the border
        // width covering the centre so only a thin animated ring shows.
        ai: `border-transparent bg-white/95 text-gray-900 isolate overflow-hidden
          before:content-[''] before:absolute before:left-1/2 before:top-1/2
          before:aspect-square before:w-[200%] before:-translate-x-1/2
          before:-translate-y-1/2 before:-z-10 before:animate-spin
          before:[animation-duration:4s]
          before:bg-[radial-gradient(circle_at_30%_30%,#6366f1_0%,#ec4899_35%,#22d3ee_65%,white_75%)]
          after:content-[''] after:absolute after:inset-[2px] after:-z-10
          after:rounded-sm after:bg-white/95`,
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'col-start-2 line-clamp-1 min-h-4 font-bold tracking-tight',
        className,
      )}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        `col-start-2 grid justify-items-start gap-1 text-sm
        [&_p]:leading-relaxed`,
        className,
      )}
      {...props}
    />
  )
}

export { Alert, AlertDescription, AlertTitle }
